import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController, NavController } from '@ionic/angular';
import { ChartOptions, ChartDataset } from 'chart.js';
import _ from 'lodash';
import moment from 'moment';

import { ComposePage } from 'src/app/components/compose/compose.page';
import { routes } from 'src/app/constants';
import {
  createCommunityRecipient,
  formatConversationContent,
  formatSlackContent,
  Recipient,
} from 'src/app/utils/utils';
import { Conversation, Customer, Tag } from 'src/models';
import { AuthService } from 'src/services/auth.service';
import { ConversationsService } from 'src/services/conversations.service';
import { CustomersService } from 'src/services/customers.service';
import { SlackService } from 'src/services/slack.service';

@Component({
  selector: 'app-customer-admin-overview',
  templateUrl: './customer-admin-overview.page.html',
  styleUrls: ['./customer-admin-overview.page.scss'],
})
export class CustomerAdminOverviewPage implements OnInit {
  customerId: string;
  customer: any;
  members: any[];
  conversations: Partial<Conversation>[];
  isDemo = false;
  slackScopeRequired = false;
  tags: Partial<Tag>[] = [];

  /*
   * Membership growth line chart
   */
  public memberChartOptions: ChartOptions = {
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };
  public memberChartData: ChartDataset[] = [
    {
      data: [],
      tension: 0,
      label: 'Members',
      backgroundColor: '#d6c8ff88',
      borderColor: '#9570ff',
      pointBackgroundColor: '#9570ff',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: '#9570ff',
    },
  ];
  public memberChartLabels: any[] = [];

  public convoChartData: ChartDataset[] = [
    {
      data: [],
      tension: 0.2,
      label: 'Conversations opened',
      backgroundColor: 'rgba(0, 128, 255, 0.2)',
      borderColor: 'rgba(0, 128, 255, 0.8)',
      pointBackgroundColor: 'rgba(0, 128, 255, 1.0)',
      pointBorderColor: '#fff',
      pointHoverBackgroundColor: '#fff',
      pointHoverBorderColor: 'rgba(0, 128, 255, 0.5)',
    },
  ];
  public convoChartLabels: string[] = [];
  public convoChartOptions: ChartOptions = {
    plugins: {
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  constructor(
    router: Router,
    private navController: NavController,
    private modalCtrl: ModalController,
    public authService: AuthService,
    private slackService: SlackService,
    private conversationsService: ConversationsService,
    private customersService: CustomersService,
  ) {
    this.customerId = router.url.split('/')[3];
  }

  async ngOnInit() {
    this.getMembers();
    this.getCustomer();
    this.getConversationList();

    this.isDemo = this.authService.subdomain === 'piedpiper';
  }

  async getCustomer() {
    const customer = (await this.customersService.get(this.customerId)) as Customer;

    this.customer = customer;

    const user = this.authService.user;

    this.tags = customer.tags;

    if (!user) {
      this.navController.navigateRoot(`/${routes.CUSTOMERS}/${this.customerId}`);
      return;
    }

    if (this.customer.slackEnabled) {
      this.slackScopeRequired = await this.slackService.requiresScope();
    }
  }

  async getMembers() {
    this.members = await this.customersService.getFollowers(this.customerId);

    this.generateMemberChart();
  }

  async sendMessage(emails?: (string | Recipient)[]) {
    if (!emails) {
      emails = [createCommunityRecipient(this.customer)];
    }

    const modal = await this.modalCtrl.create({
      component: ComposePage,
      componentProps: {
        communityId: this.customerId,
        showTemplates: true,
        emails,
      },
      cssClass: 'modal-large',
    });
    modal.present();
  }

  shouldShowSlack() {
    return this.authService.integrations.slackEnabled && !this.slackScopeRequired;
  }

  createSlack() {
    this.navController.navigateForward(`${routes.SLACK_MESSAGE}/new`, {
      queryParams: {
        communityId: this.customerId,
      },
    });
  }

  connectToSlack() {
    this.slackService.oauth(window.location.href, window.location.origin, undefined);
  }

  generateMemberChart() {
    const totalDays = 60;
    const today = moment();
    const runningDate = moment().subtract(totalDays, 'days');
    const dailyFollowers = [];
    const labels = [];

    for (let i = 0; i < totalDays; i++) {
      dailyFollowers.push(0);
      runningDate.add(1, 'days');
      labels.push(runningDate.format('M/D'));
    }

    this.members.map((communityMember) => {
      if (!communityMember.updatedAt) {
        return;
      }

      const diff = Math.abs(moment(communityMember.updatedAt).diff(today, 'days'));

      // account for 0 index and for growth happening day before
      const index = totalDays - diff - 2;

      if (index >= 0 && index < totalDays) {
        const change = communityMember.following ? 1 : -1;

        dailyFollowers[totalDays - diff - 2] = dailyFollowers[totalDays - diff - 2] + change;
      }
    });

    const followersTotal = [];
    let runningTotal = this.members.length;

    for (let i = dailyFollowers.length - 1; i >= 0; i--) {
      const newFollowers = dailyFollowers[i];

      if (newFollowers) {
        runningTotal = runningTotal - newFollowers;
      }

      followersTotal.unshift(runningTotal);
    }

    this.memberChartData[0].data = followersTotal;
    this.memberChartLabels = labels;
  }

  generateConversationHistoryChart() {
    const totalDays = 60;
    const today = moment();
    const runningDate = moment().subtract(totalDays, 'days');
    const dailyConvos = [];
    const labels = [];
    for (let i = 0; i < totalDays; i++) {
      dailyConvos.push(0);
      runningDate.add(1, 'days');
      labels.push(runningDate.format('M/D'));
    }

    this.conversations.map((convo) => {
      const diff = Math.abs(moment(convo.createdAt).diff(today, 'days'));

      // account for 0 index and for growth happening day before
      const index = totalDays - diff - 1;
      if (index >= 0 && index < totalDays) {
        dailyConvos[index] = dailyConvos[index] + 1;
      }
    });

    this.convoChartData[0].data = dailyConvos;
    this.convoChartLabels = labels;
  }

  async getConversationList() {
    const options: any = {
      limit: 50,
      order: ['createdAt'],
      descending: true,
      select: [
        'id',
        'respondedAt',
        'closedAt',
        'createdAt',
        'friendlyId',
        'resolvedTitle',
        'initialMessage.id',
        'initialMessage.text',
        'initialMessage.content',
        'status',
        'assignedToUser.id',
        'assignedToUser.name',
      ],
    };

    options.where = [
      {
        field: 'customerId',
        operator: '==',
        value: this.customerId,
      },
    ];

    try {
      const convos = await this.conversationsService.list(options);
      this.conversations = convos.data;
      this.generateConversationHistoryChart();
    } catch (e) {
      console.log('Error loading conversations: ', e);
    }
  }

  viewMembers() {
    const path = `${routes.DASHBOARD}/${routes.CUSTOMERS}/${this.customerId}/members`;

    this.navController.navigateRoot(path);
  }

  formatContent(conversation: any, maxLength?: number) {
    return formatConversationContent(conversation, maxLength);
  }
}
